import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, requiredInt, upper } from './context.js';
import { type JanusInput } from './janus.js';
import { applyAbilityEffectToTarget } from './apply-ability-effect-to-target.js';
import { applyStatusEffects } from './statuses/apply-status-effects.js';
import { removeStatusEffects, removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { statusItemId } from '../statuses/expiry.js';
import { plCatalogs } from '../pl/contract.js';
import { nullifyPostEffectConditions } from './nullify-post-effect-conditions.js';
import { checkPushAndPullAfterHit } from './movement/check-push-and-pull-after-hit.js';
import { applyChangeSpeedRemainingLayersToCaster } from './movement/apply-change-speed-remaining-layers-to-caster.js';
import { SUMMON_LAYERS, SUMMON_ROLL, isSummonAbility, isSummonPointTarget, planOneSummon } from './summon.js';
import { addDirectCharges } from './charges/adjust-charges.js';
import { applyChangeHungerThirst } from './change-meters.js';
import { runK4PostRoll } from './k4-dispatch.js';
import { runStandingOpportunityReactions } from './k4-boundary-reactions.js';
import { applyTargetResources } from './target-resource-effects.js';
import { queueNextSpellTarget } from './next-spell.js';
import { applyShapeshiftPostEffect, canCommitShapeshift, SHAPESHIFT_LAYERS } from './shapeshift.js';
const PRONE = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';

export const POST_EFFECT_UNSUPPORTED_LAYERS: readonly string[] = ['summon', 'user_choice_1_summon', 'user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon', 'swap_positions_with_target', 'caster_special_movement', 'teleport', 'ap_change_to_target_during_turn', 'weapon_attack_count_change', 'orb_channel', 'add_charges_to_ability', 'consume_charges_of_ability', 'change_luck_for_target'];

/** includes/functions/janus/core/post_effect_conditions.php:post_effect_conditions — returns $post_effect_conditions_met (the plain damage
 *  roll applies its damage only when met: calc_total_damage_and_apply_to_target runs at the END of post_effect_conditions). */
export function postEffectConditions(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, contestSucceeded = false, sharedGate?: boolean): boolean {
  // a SUMMON's ground point (summon.ts) is no creature: none of the target gates read it (summonPreDraw handed every target-gated shape back)
  const groundSummon = isSummonPointTarget(target);
  const postEffectConditionsMet = groundSummon ? true : sharedGate ?? nullifyPostEffectConditions(ctx, ability, caster, target);
  runK4PostRoll(ctx, input, ability, caster);
  if (!postEffectConditionsMet) return false;
  queueNextSpellTarget(ability, target);
  if (!canCommitShapeshift(ctx) && SHAPESHIFT_LAYERS.some(key => ctx.read.has(ability, key))) handBack('post_effect_conditions:shapeshift');
  // Wizard v9 WP3: a summon is decided here and persisted by the commit (summon.ts) wherever the caller can persist one (ctx.summons)
  // add_charges_to_ability: a trigger frame granted it at janus()'s entry (PHP suppresses this copy); a direct use grants it below
  // (Wizard Channel casts, 2026-10-09) wherever the caller's commit persists charges + the marker status (the `statusStacks` capability)
  for (const key of POST_EFFECT_UNSUPPORTED_LAYERS) if (ctx.read.has(ability, key)
    && !(key === 'add_charges_to_ability' && (ctx.trigger || ctx.statusStacks !== undefined))
    && !(ctx.summons != null && SUMMON_LAYERS.includes(key))) handBack(`post_effect_conditions:${key}`);
  applyChangeSpeedRemainingLayersToCaster(ctx, ability, caster);
  if (ctx.read.has(ability, 'spell_concentration') && !ctx.read.has(ability, 'persistent_aoe'))
    applyStatusEffects(ctx, input, ability, 'spell_concentration', caster, caster);
  if (contestSucceeded)
    applyStatusEffects(ctx, input, ability, 'status_effect_applied_to_target_after_contesting_roll_failed', caster, target);
  checkApChangeDuringTurn(ctx, input, ability, caster, target);
  // A ground cast's maintaining source is attached to its first native spawned creature by the shared row planner.
  if (!groundSummon) applyAbilityEffectToTarget(ctx, input, ability, caster, target);
  const standing = input.abilityId === '21D8FBA4-AD2E-EA72-965D-E0445CBFF722'
    && activeStatuses(ctx, target).some(status => statusItemId(status) === PRONE);
  for (const [key, recipient] of [
    ['status_effect_removed_from_caster_after_cast', caster], ['status_effect_removed_from_target_after_cast', target],
    ['status_effect_removed_from_caster_on_hit', caster], ['status_effect_removed_from_target_on_hit', target],
  ] as const) {
    removeStatusEffects(ctx, ability, key, recipient);
    if (key === 'status_effect_removed_from_target_after_cast' && standing
        && !activeStatuses(ctx, target).some(status => statusItemId(status) === PRONE))
      runStandingOpportunityReactions(ctx, target);
  }
  const count = Number(ctx.read.value(ability, 'remove_random_bane') ?? 0);
  if (count > 0) {
    // ft_remove_random_banes uses the canonical catalog (ability-as-status rows never qualify), including paused rows.
    const pool = [...ctx.read.statuses(target), ...ctx.statusesAdded.filter(s => upper(s.target) === ecId(target))]
      .filter(s => plCatalogs().statusEffects.getById(statusItemId(s as Row))?.status_type === 'bane');
    for (let i = 0; i < Math.min(Math.trunc(count), pool.length); i++) {
      if (i < pool.length - 1) {
        const drawn = ctx.roll.d(pool.length - i), j = i + (ctx.randomPickProbe?.(pool.length - i, drawn) ?? drawn) - 1;
        [pool[i], pool[j]] = [pool[j]!, pool[i]!];
      }
      removeStoredStatus(ctx, target, pool[i]! as Row);
    }
  }
  for (const [key, recipient] of [
    ['status_effect_applied_to_caster_after_cast', caster],
    // passive_ability_applied_to_caster_after_cast (Psion Pyrokinetic Fortress, 2026-10-07 — PHP post_effect_conditions.php, R-181): each
    // listed PASSIVE lands on the caster as its own status row under this use's connection, with the use's duration. A persistent area
    // applies it once, when the zone is placed (persistent-aoe.ts applyPersistentAoeCasterPassives), never per occupant.
    ['passive_ability_applied_to_caster_after_cast', caster],
    ['status_effect_applied_to_target_after_cast', target],
    ['status_effect_applied_to_caster_on_hit', caster], ['status_effect_applied_to_target_on_hit', target],
  ] as const) {
    if (key === 'passive_ability_applied_to_caster_after_cast' && ctx.read.has(ability, 'persistent_aoe')) continue;
    applyStatusEffects(ctx, input, ability, key, caster, recipient);
  }
  // post_effect_conditions.php ~L879: add_charges_to_ability() for a DIRECT use, after the statuses (a Channel's Holding Mastery row lands
  // first, so the grant's marker touch finds it pending) — never in a trigger frame (janus()'s entry already granted it)
  if (!ctx.trigger && ctx.read.has(ability, 'add_charges_to_ability')) addDirectCharges(ctx, ability, caster, input.useAbilityVariable1);
  applyChangeHungerThirst(ctx, input, ability, caster, target);
  applyTargetResources(ctx, ability, target, input.useAbilityVariable1);
  // janus_summon(): once for an ability that summons without a Summon Roll, `roll_result` times for a Summon Roll (summon-roll.ts)
  if (ctx.summons != null && isSummonAbility(ctx, ability)) {
    const rolled = String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase() === SUMMON_ROLL;
    const count = rolled ? Math.max(0, Math.trunc(ctx.summonRollResult ?? 0)) : 1;
    for (let i = 0; i < count; i++) planOneSummon(ctx, input, ability, caster, target, i);
  }
  applyShapeshiftPostEffect(ctx, input, ability, caster, target);
  checkPushAndPullAfterHit(ctx, input, ability, caster, target);
  return true;
}

/**
 * check_ap_change_during_turn.php (lane A10, 2026-10-02 — found by the orb-evoke capture: Fire Orb Surge's +1 AP; the post-effect pass
 * dropped this layer SILENTLY before, on every served ability carrying it). After the contest-failed statuses, before the ability's own
 * duration status: reaction_reset '1' or the legacy 'r' sentinel restores the target's reaction (logged `reaction_gained`); an integer delta
 * moves the target's CURRENT aps (floored at 0, never capped — a mid-turn grant may exceed aps_total), logged `ap_change_during_turn` with the
 * signed delta. A templated / non-integer value ("{caster_level}") is not modelled → PHP.
 */
function checkApChangeDuringTurn(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  const resetReaction = String(ctx.read.value(ability, 'reaction_reset') ?? '') === '1';
  const hasChange = ctx.read.has(ability, 'ap_change_during_turn');
  if (!hasChange && !resetReaction) return;
  const change = hasChange ? ctx.read.value(ability, 'ap_change_during_turn') : null;
  if (!ecId(target)) handBack('check_ap_change_during_turn:target-not-member');
  if (resetReaction || change === 'r') {
    ec(target).reaction = 1;
    insertRoll(ctx, { roll_type_1: 'reaction_gained', roll_item_id: input.abilityId, by: caster, to: target });
  }
  if (hasChange && change !== 'r') {
    if (!/^[+-]?\d+$/.test(String(change ?? '').trim())) handBack('check_ap_change_during_turn:non-integer');
    const delta = Number.parseInt(String(change).trim(), 10);
    const aps = requiredInt(ecLean(target, 'aps'), 'check_ap_change_during_turn:aps-not-held');
    ec(target).aps = Math.max(0, aps + delta);
    insertRoll(ctx, { roll_type_1: 'ap_change_during_turn', roll_type_2: '', roll_item_id: input.abilityId, by: caster, to: target, roll_result: delta });
  }
}
